import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useTimesheetMutations } from '@/hooks/timesheets';

interface TimeEntry {
  id: string;
  clock_in: string;
  clock_out: string | null;
  entry_type?: 'automatic' | 'manual';
  status?: string;
}

interface EditOwnTimesheetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: TimeEntry | null;
}

const schema = z.object({
  clock_in: z.string().min(1, 'Clock in time is required'),
  clock_out: z.string().min(1, 'Clock out time is required'),
}).refine((data) => {
  const clockIn = new Date(data.clock_in);
  const clockOut = new Date(data.clock_out);
  return clockOut > clockIn;
}, {
  message: 'Clock out must be after clock in',
  path: ['clock_out'],
});

type FormData = z.infer<typeof schema>;

export function EditOwnTimesheetDialog({ 
  open, 
  onOpenChange, 
  entry 
}: EditOwnTimesheetDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { editOwnTimeEntry } = useTimesheetMutations();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      clock_in: '',
      clock_out: '',
    },
  });

  // Format datetime for input
  const formatDateTimeLocal = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Reset form when entry changes
  useEffect(() => {
    if (entry && open) {
      form.reset({
        clock_in: formatDateTimeLocal(entry.clock_in),
        clock_out: entry.clock_out ? formatDateTimeLocal(entry.clock_out) : '',
      });
    }
  }, [entry, open, form]);

  const onSubmit = async (data: FormData) => {
    if (!entry) return;

    setIsSubmitting(true);
    try {
      await editOwnTimeEntry.mutateAsync({
        entryId: entry.id,
        clockIn: new Date(data.clock_in).toISOString(),
        clockOut: new Date(data.clock_out).toISOString(),
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating timesheet:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!entry) return null;

  // Don't allow editing approved entries
  const isApproved = entry.status === 'approved';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Time Entry</DialogTitle>
        </DialogHeader>

        {isApproved ? (
          <div className="py-4 text-center text-muted-foreground">
            <p>This time entry has been approved and cannot be edited.</p>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="clock_in"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Clock In</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="clock_out"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Clock Out</FormLabel>
                    <FormControl>
                      <Input type="datetime-local" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}